/**
 * Number formatting for the eval analysis panel: "$1.87", "9.6M", "39.8s".
 * Kept from the retired agent Overview, whose cards it formatted.
 */

/** 9571583 -> "9.6M". Axis labels and tiles can't afford 7 digits. */
export function compact(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
  return String(Math.round(v));
}

/**
 * Judge and token costs are fractions of a cent, so a fixed 2dp renders "$0.00"
 * for a real number and a fixed 5dp spends the width on leading zeros.
 */
export function currency(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  if (v === 0) return '$0';
  if (Math.abs(v) < 0.01) return `${(v * 100).toFixed(2)}¢`;
  if (Math.abs(v) < 1000) return `$${v.toFixed(2)}`;
  return `$${compact(v)}`;
}

/** ms -> "39.8s" / "2.3m". Agent runs take seconds to minutes, not ms. */
export function duration(ms) {
  const v = Number(ms);
  if (!Number.isFinite(v)) return '—';
  if (v < 1000) return `${Math.round(v)}ms`;
  if (v < 60_000) return `${(v / 1000).toFixed(1)}s`;
  if (v < 3_600_000) return `${(v / 60_000).toFixed(1)}m`;
  return `${(v / 3_600_000).toFixed(1)}h`;
}

export function percent(n, dp = 1) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `${v.toFixed(dp)}%`;
}

/** Format by the unit a chart declares. */
export function formatValue(value, unit) {
  if (value == null) return '—';
  switch (unit) {
    case 'currency': return currency(value);
    case 'duration_ms': return duration(value);
    case 'percent': return percent(value);
    case 'compact': return compact(value);
    default: {
      const v = Number(value);
      return Number.isFinite(v) ? v.toLocaleString() : String(value);
    }
  }
}

/**
 * Change vs the preceding window.
 *
 * Returns null when there's nothing honest to say: no previous value, or a
 * previous of zero — "+∞%" against a zero baseline is noise, and rendering
 * "+100%" for 0→5 implies a rate that doesn't exist.
 */
export function delta(current, previous) {
  const c = Number(current);
  const p = Number(previous);
  if (!Number.isFinite(c) || !Number.isFinite(p) || p === 0) return null;
  const pct = ((c - p) / Math.abs(p)) * 100;
  if (!Number.isFinite(pct)) return null;
  return { pct, up: pct > 0, flat: Math.abs(pct) < 0.05 };
}

/** Bucket labels: "14 Jul" for days, "09:00" for hours. */
export function bucketLabel(iso, bucketSeconds) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  if (bucketSeconds <= 3600) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}
